import { supabase } from '../lib/supabase';
import { DbAudioGenerationJob } from '../lib/supabase';
import { logger } from '../utils/logger';

/**
 * Audio Job Service
 *
 * Manages server-side audio generation jobs via Supabase Edge Function.
 * Provides job creation, progress tracking (via Realtime), and cancellation.
 */

export interface AudioJobProgress {
  jobId: string;
  status: DbAudioGenerationJob['status'];
  totalItems: number;
  completedItems: number;
  failedItems: number;
  errorMessage?: string;
}

/**
 * Create a new audio generation job record
 */
export async function createAudioJob(quizId: string): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('audio_generation_jobs')
    .insert({
      quiz_id: quizId,
      status: 'pending',
      created_by: user.id,
    })
    .select('id')
    .single();

  if (error) {
    throw new Error(`Failed to create audio job: ${error.message}`);
  }

  logger.log(`Created audio job: ${data.id} for quiz: ${quizId}`);
  return data.id;
}

/**
 * Trigger the edge function to start generating audio
 */
export async function triggerAudioGeneration(
  quizId: string,
  jobId: string
): Promise<void> {
  const { error } = await supabase.functions.invoke('generate-quiz-audio', {
    body: { quizId, jobId },
  });

  if (error) {
    // Update job status to failed
    await supabase.from('audio_generation_jobs').update({
      status: 'failed',
      error_message: `Failed to invoke edge function: ${error.message}`,
      updated_at: new Date().toISOString(),
    }).eq('id', jobId);

    throw new Error(`Failed to trigger audio generation: ${error.message}`);
  }
}

/**
 * Get current job status (polling fallback)
 */
export async function getJobStatus(jobId: string): Promise<AudioJobProgress> {
  const { data, error } = await supabase
    .from('audio_generation_jobs')
    .select('*')
    .eq('id', jobId)
    .single();

  if (error || !data) {
    throw new Error(`Failed to get job status: ${error?.message || 'Not found'}`);
  }

  return {
    jobId: data.id,
    status: data.status,
    totalItems: data.total_items,
    completedItems: data.completed_items,
    failedItems: data.failed_items,
    errorMessage: data.error_message || undefined,
  };
}

/**
 * Subscribe to job progress via Supabase Realtime
 * Returns an unsubscribe function.
 */
export function subscribeToJob(
  jobId: string,
  callback: (progress: AudioJobProgress) => void
): () => void {
  const channel = supabase
    .channel(`audio-job-${jobId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'audio_generation_jobs',
        filter: `id=eq.${jobId}`,
      },
      (payload) => {
        const job = payload.new as DbAudioGenerationJob;
        callback({
          jobId: job.id,
          status: job.status,
          totalItems: job.total_items,
          completedItems: job.completed_items,
          failedItems: job.failed_items,
          errorMessage: job.error_message || undefined,
        });
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Cancel an in-progress job
 */
export async function cancelJob(jobId: string): Promise<void> {
  const { error } = await supabase
    .from('audio_generation_jobs')
    .update({
      status: 'cancelled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', jobId);

  if (error) {
    throw new Error(`Failed to cancel job: ${error.message}`);
  }

  logger.log(`Cancelled audio job: ${jobId}`);
}

/**
 * Generate audio for a module using server-side edge function.
 * This is the main entry point — creates a job, triggers generation,
 * and returns subscription for progress tracking.
 */
export async function generateAudioServerSide(
  quizId: string,
  onProgress: (progress: AudioJobProgress) => void
): Promise<{ jobId: string; unsubscribe: () => void }> {
  // Create the job
  const jobId = await createAudioJob(quizId);

  // Subscribe to progress updates
  const unsubscribe = subscribeToJob(jobId, onProgress);

  // The edge function does ALL its work inside this request — when the invoke
  // resolves, the job is finished. Deliver the final row state to the callback
  // directly rather than hoping Realtime or the next poll tick is alive to do
  // it: a fast all-skipped run completed before the listeners settled and left
  // the progress banner stranded on screen (22 Jul). This path has no timers
  // to race; Realtime and polling remain as fallbacks for network drops.
  triggerAudioGeneration(quizId, jobId)
    .then(async () => {
      try {
        onProgress(await getJobStatus(jobId));
      } catch (error) {
        logger.log('Post-invoke status check failed (Realtime/polling will cover):', error);
      }
    })
    .catch((error) => {
      logger.error('Edge function invocation failed:', error);
      onProgress({
        jobId,
        status: 'failed',
        totalItems: 0,
        completedItems: 0,
        failedItems: 0,
        errorMessage: error instanceof Error ? error.message : 'Generation failed',
      });
    });

  // Also set up polling fallback in case Realtime doesn't connect
  let stuckCheckStart: number | null = null;
  const STUCK_TIMEOUT_MS = 30_000; // 30 seconds of "all items done but no completion" = treat as completed

  const pollInterval = setInterval(async () => {
    try {
      const status = await getJobStatus(jobId);
      onProgress(status);

      if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
        clearInterval(pollInterval);
        return;
      }

      // Detect stuck jobs: all items processed but status still 'processing'
      if (status.status === 'processing' && status.totalItems > 0 &&
          status.completedItems + status.failedItems >= status.totalItems) {
        if (!stuckCheckStart) {
          stuckCheckStart = Date.now();
        } else if (Date.now() - stuckCheckStart > STUCK_TIMEOUT_MS) {
          logger.log(`Job ${jobId} appears stuck (all items done but status is processing). Treating as completed.`);
          clearInterval(pollInterval);
          onProgress({ ...status, status: status.failedItems > 0 && status.completedItems === 0 ? 'failed' : 'completed' });
        }
      } else {
        stuckCheckStart = null;
      }
    } catch (error) {
      logger.log('Audio job polling error (will retry):', error);
    }
  }, 3000);

  // Return cleanup function that stops both Realtime and polling
  const cleanup = () => {
    unsubscribe();
    clearInterval(pollInterval);
  };

  return { jobId, unsubscribe: cleanup };
}
